import {CouponInfoSheet} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/coupon.types';
import {COLORS, SPACING, useInViewEffect} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {useRef} from 'react';
import {CoinsWalletContent} from '../../features/Coins/CoinsWallet/CoinsWalletContent/CoinsWalletContent';
import {CoinsWalletHeader} from '../../features/Coins/CoinsWallet/CoinsWalletHeader/CoinsWalletHeader';
import {RequestFailure} from '../../features/Coins/shared/RequestFailure';
import {CouponWalletContent} from '../../features/Coupons/CouponWallet/CouponWalletContent';
import {CouponWalletHeader} from '../../features/Coupons/CouponWallet/CouponWalletHeader';
import {NotificationCenterContent} from '../NotificationCenter/Stream/NotificationCenterContent';
import {WalletProps} from './Wallet';
import {WalletType} from './utils/useActiveTab';
import {NormalizedWalletTab} from './utils/useNormalizeTabs';

export const WalletContent: React.FC<
  Omit<WalletProps, 'ContentPopperLayoutComponent'> & {
    activeTab: WalletType;
    handleCouponInfoIconClick: (couponInfo?: CouponInfoSheet) => void;
    setSelectedProduct?: WalletProps['setSelectedProduct'];
    tabs: NormalizedWalletTab[];
    setHasScrolled: (hasScrolled: boolean) => void;
  }
> = ({
  activeTab,
  tabs,
  setSelectedProduct,
  handleCouponInfoIconClick,
  setHasScrolled,
}) => {
  const onCloseInfoSheet = useRef<VoidFunction>(undefined);
  const currentTab = tabs.find(tab => tab.value === activeTab);
  const {ref} = useInViewEffect<HTMLDivElement>(inView =>
    setHasScrolled(!inView),
  );

  if (!currentTab) {
    return null;
  }

  switch (currentTab.type?.$case) {
    case 'coins':
      return (
        <>
          <StyledCoinsWalletHeader {...currentTab.type.coins.header} />
          <ScrollerTracker ref={ref} />
          <RoundedBodyWrapper>
            <CoinsWalletContent sections={currentTab.type.coins.sections} />
          </RoundedBodyWrapper>
        </>
      );

    case 'coupons': {
      const Wrapper = ['emptyCouponSection', 'couponsPausedSection'].includes(
        currentTab.type.coupons.header.type?.$case ?? '',
      )
        ? React.Fragment
        : RoundedBodyWrapper;

      return (
        <>
          <ScrollerTracker ref={ref} />
          <CouponWalletHeader
            header={currentTab.type.coupons.header}
            setSelectedProduct={setSelectedProduct}
            paused={!!currentTab.pausedModal}
            handleCouponInfoClick={handleCouponInfoIconClick}
            onCloseInfoSheet={onCloseInfoSheet}
          />
          <Wrapper>
            <CouponWalletContent
              sections={currentTab.type.coupons.sections}
              setSelectedProduct={setSelectedProduct}
              paused={!!currentTab.pausedModal}
              handleInfoIconClick={handleCouponInfoIconClick}
            />
          </Wrapper>
        </>
      );
    }
    case 'notificationCenter':
      return (
        <NotificationBodyWrapper>
          <ScrollerTracker ref={ref} />
          <NotificationCenterContent
            notifications={currentTab.type.notificationCenter.notifications}
          />
        </NotificationBodyWrapper>
      );
    case 'coinsError':
    case 'couponsError':
    case 'notificationCenterError':
      return <StyledRequestFailure />;
    default:
      return null;
  }
};

const RoundedBodyWrapper = styled.div`
  border-radius: 16px 16px 0px 0px;
  background-color: ${COLORS.White};
  padding: ${SPACING['Y.Lg']} ${SPACING['X.Lg']};
  flex: 1;
  z-index: 1;
`;

const ScrollerTracker = styled.div`
  height: 1px;
  opacity: 0;
`;

const NotificationBodyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  background-color: ${COLORS['Fill.Grey.Primary']};
`;

const StyledRequestFailure = styled(RequestFailure)`
  padding-top: 8rem;
  height: 100%;
  background-color: ${COLORS['Fill.Grey.Primary']};
`;

const StyledCoinsWalletHeader = styled(CoinsWalletHeader)`
  position: sticky;
  top: 0;
`;
